import { Icon, Link, Button, Text, Box, Flex, Heading, Container, AspectRatio } from '@chakra-ui/react';
import Image from 'next/image';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import ContactUs from '@/assets/icons/ic-nav-help-desk.svg';

import { EventTicketDescriptionMarkdown } from '@/components/ui/shared/markdowns/EventTicketDescriptionMarkdown/EventTicketDescriptionMarkdown';
import { ThinEvent } from '@/resources/ThinEvent';

interface INoTicketProps {
  event: ThinEvent;
}

export const UserNotLoggedIn: FC<INoTicketProps> = ({ event }) => {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <Container>
      <Flex justifyContent="center" alignItems={{ base: 'flex-start', lg: 'center' }} minH="calc(100vh - 72px)" py={7}>
        <Flex
          flexDir={{ base: 'column', lg: 'row' }}
          gap={{ base: 7, lg: 12 }}
          w="full"
          flex={1}
          h={{ base: 'auto', lg: '430px' }}
          justifyContent="center"
          alignItems="center"
        >
          <AspectRatio
            w="full"
            maxW={{ base: 'auto', lg: '626px' }}
            h={{ base: '160px', sm: '330px', lg: '430px' }}
            ratio={4 / 3}
          >
            {event?.mediumHeaderImageUrl ? (
              <Image
                sizes="(max-width: 480px) 160px, (max-width: 1200px) 330px, 430px"
                priority
                fill
                src={event?.mediumHeaderImageUrl}
                alt={event?.name}
              />
            ) : (
              <Box />
            )}
          </AspectRatio>
          <Flex flexDir="column" flex={1} justifyContent="space-around" h="full">
            <Heading mb={{ base: 7, lg: 0 }} fontSize={{ base: '2xl', md: '3xl' }}>
              {event?.guestMissingTicketTitle || 'Please log in to explore this event'}
            </Heading>
            <Box shadow="card" bgColor="white" borderRadius={2} p={4}>
              <Box borderRadius={2} bgColor="gray.50" p={4} mb={{ base: 4, lg: 6 }}>
                <Text>
                  {event?.guestMissingTicketDescription ? (
                    <EventTicketDescriptionMarkdown source={event.guestMissingTicketDescription} />
                  ) : (
                    <Text as="span">
                      You need to log in with the email address you registered with. Access credentials have been sent
                      to your email.{' '}
                      <Text fontWeight="bold" as="span">
                        Note:
                      </Text>{' '}
                      It may still be early and access has not yet been granted.
                    </Text>
                  )}
                </Text>
              </Box>
              <Flex flexDir={{ base: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" w="full">
                <NextLink href={`/log-in?redirectUrl=${router.asPath}`} passHref legacyBehavior>
                  <Button as="a" size="sm" w={{ base: 'full', sm: 'auto' }} minW="200px">
                    {t('eventTicketRegister.logIn')}
                  </Button>
                </NextLink>
                <Flex alignItems="center" mt={{ base: 5, sm: 0 }}>
                  <Text mr={4}>{t('eventTicketRegister.needHelp')}</Text>
                  <NextLink
                    href={{
                      pathname: '/contact-us/support',
                      query: { eventId: event.id },
                    }}
                    passHref
                    legacyBehavior
                  >
                    <Link fontWeight="bold">
                      <Flex alignItems="center" mb={1}>
                        {t('eventTicketRegister.contactUs')} <Icon as={ContactUs} ml={2} />
                      </Flex>
                    </Link>
                  </NextLink>
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </Container>
  );
};
